import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import {
  getAvailableMonthsStartingPrices,
  getDepartureYears,
  getDeparturesCheapestPurchasableOptions,
  getDeparturesOccupanciesPriceBreakdowns,
  getDeparturesAccommodableOptions,
  getSelectableOccupancies,
  getSelectedDepartureOccupanciesOptions,
  getDepartureMonthIndicesToDisplay,
  getCategorisedDepartures,
  getDepartureYearsDisplayableMonthIndices,
  hasPackageUpgradeInSelectableOccupancies,
} from 'selectors/TourV2/tourV2OfferPageDeparturesSelectors'
import { getHasAllOccupanciesOptionsSelected, getOccupanciesTotalPrice } from 'contexts/TourV2/RoomSelection/tourV2RoomSelectionStateSelectors'
import { getHasChildExceedingAgeRange } from 'selectors/TourV2/tourV2OfferPageTravellersSelectors'
import { getCheapestPurchasableOption } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { TourV2RoomSelectionAction, TourV2RoomSelectionActions } from 'contexts/TourV2/RoomSelection/tourV2RoomSelectionStateReducer'
import { TourV2RoomSelectionState } from 'contexts/TourV2/RoomSelection/tourV2RoomSelectionState'
import Heading from 'components/Luxkit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import DepartureCard from './DepartureCard'
import InvalidTravellersMessagePane from './MessagePanes/InvalidTravellersMessagePane'
import RoomSelectionModal from './RoomSelection/RoomSelectionModal'
import TourV2TravellersSelect from './TourV2TravellersSelect'
import { SecureWithDepositBanner } from 'components/Common/SecureWithDeposit/SecureWithDepositBanner'
import CurrencyContext from 'contexts/currencyContext'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import isTourV2DepositAllowed from 'lib/TourV2/isTourV2DepositAllowed'
import TourV2MonthDepartures from './TourV2MonthDepartures'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import MonthTabButtonsGroup from './MonthTabs/MonthTabButtonsGroup'
import MonthTabButton from './MonthTabs/MonthTabButton'
import { sum } from 'lib/arrayUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import { MONTH_NAMES } from 'constants/dates'
import usePrevious from 'hooks/usePrevious'
import { TOUR_V2_DEFAULT_OCCUPANCIES } from 'constants/tours'
import { areOccupantsEqual, generateRoomOccupants } from 'lib/occupancyUtils'
import Modal from 'components/Luxkit/Modal/Modal'
import useToggle from 'hooks/useToggle'
import BodyText from 'components/Luxkit/Typography/BodyText'
import * as Analytics from 'analytics/analytics'
import { addToCartEvent } from 'analytics/optimizely/events'
import config from 'constants/config'

const INITIAL_MAX_DEPARTURES_TO_SHOW = 6

const Root = styled(LayoutContainer)`
  margin-bottom: ${rem(40)};
  margin-top: ${rem(40)};

  &.md {
    max-width: ${rem(1140)};
  }
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: center;
`

const ControlsContainer = styled.div`
  position: relative; /* traveller select relies on this */
  display: flex;
  flex-direction: column;
  margin-top: ${rem(40)};
  gap: ${rem(16)};

  ${mediaQueryUp.sm} {
    flex-direction: row;
    justify-content: space-between;
  }
`

const YearTabButtonsGroup = styled(TabButtonsGroup)`
  ${mediaQueryUp.sm} {
    max-width: ${rem(424)};
  }
`

const MonthPickerContainer = styled.div`
  margin-top: ${rem(16)};
`

const SecureWithDepositBannerContainer = styled.div`
  margin-top: ${rem(32)};
`

const MonthDepartures = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  &.is-hidden {
    display: none;
  }
`

const DeparturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(40)};
  gap: ${rem(40)};

  &:empty {
    display: none;
  }
`

const DeparturesFooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(40)};

  ${mediaQueryUp.sm} {
    flex-direction: row;
    justify-content: center;
  }
`

const MessagesContainer = styled.div`
  margin-top: ${rem(24)};

  ${mediaQueryUp.sm} {
    margin-top: ${rem(40)};
  }

  &:empty {
    display: none;
  }
`

const SelectedDepartureContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(40)};
`

interface MappedStateProps {
  availableMonthsStartingPrices: Map<App.MonthIndex, number>
  departureYears: number[]
  cheapestPurchasableOption?: Tours.V2OfferPurchasableOption
  departuresAccommodableOptions: Map<Tours.V2OfferDeparture, Tours.V2OfferAccommodablePurchasableOptions>
  departuresCheapestPurchasableOptions: Map<Tours.V2OfferDeparture, Tours.V2OfferPurchasableOption>
  departuresOccupanciesPriceBreakdowns: Map<Tours.V2OfferDeparture, Map<App.Occupants, Map<Tours.V2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>>>
  hasAllOccupanciesOptionsSelected: boolean
  hasChildExceedingMinAge: boolean
  occupanciesTotalPrice: number
  offer: Tours.V2Offer
  rooms?: OfferPageState['rooms']
  selectableOccupancies: App.Occupants[]
  selectedDeparture: Tours.V2OfferDeparture
  selectedDepartureOccupanciesOptions: Map<App.Occupants, Map<Tours.V2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>>
  hasPackageUpgrade: boolean
  selectedDepartureMonthIndex: App.MonthIndex
  selectedVariation: Tours.V2OfferVariation
  selectedDepartureYear: number
  departureMonthIndicesToDisplay: Set<App.MonthIndex>
  categorisedDepartures: Tours.V2OfferCategorisedDepartures
  departureYearsDisplayableMonthIndices: Map<number, App.MonthIndex[]>
}

interface Props {
  roomSelectionState: TourV2RoomSelectionState
  roomSelectionStateDispatch: React.Dispatch<TourV2RoomSelectionAction>
  onProceedToCheckout: (
    startDate: string,
    endDate: string,
    occupantsPurchasableOptions: Map<App.Occupants, Tours.V2OfferPurchasableOption>,
    priceTotal: number
  ) => void
  onProceedToPackageUpgrade: (
    selectedDeparture: Tours.V2OfferDeparture,
    selectableOccupancies: App.Occupants[],
  ) => void
  onDepartureSelect: (departure: Tours.V2OfferDeparture) => void
}

function TourV2Departures(props: Props & MappedStateProps) {
  const {
    availableMonthsStartingPrices,
    categorisedDepartures,
    cheapestPurchasableOption,
    departureMonthIndicesToDisplay,
    departuresCheapestPurchasableOptions,
    departuresOccupanciesPriceBreakdowns,
    departureYears,
    departureYearsDisplayableMonthIndices,
    hasAllOccupanciesOptionsSelected,
    hasChildExceedingMinAge,
    occupanciesTotalPrice,
    offer,
    onDepartureSelect,
    onProceedToCheckout,
    rooms,
    roomSelectionState,
    roomSelectionStateDispatch,
    selectableOccupancies,
    selectedDeparture,
    selectedDepartureMonthIndex,
    selectedDepartureOccupanciesOptions,
    hasPackageUpgrade,
    selectedDepartureYear,
    selectedVariation,
    onProceedToPackageUpgrade,
  } = props

  const offerPageDispatch = useOfferPageDispatch()

  const monthTabsRef = useRef<HTMLDivElement>()
  const controlsScrollAnchorRef = useRef<HTMLDivElement>()
  const currentCurrency = useContext(CurrencyContext)

  const [maxDeparturesToShow, setMaxDeparturesToShow] = useState<number>(INITIAL_MAX_DEPARTURES_TO_SHOW)
  const [selectedRoomsPostAcknowledgement, selectRoomsPostAcknowledgement] = useState<App.Occupants[]>(null)
  const [tempSelectedDeparture, selectTempDeparture] = useState<Tours.V2OfferDeparture | null>(null)
  const [isGuaranteedDisclosureModalOpen, , openGuaranteedDisclosureModal, closeGuaranteedDisclosureModal] = useToggle()

  const previousRooms = usePrevious(rooms)

  const hasSelectedDefaultRooms = useMemo<boolean>(() => !!selectedRoomsPostAcknowledgement &&
    selectedRoomsPostAcknowledgement.length === TOUR_V2_DEFAULT_OCCUPANCIES.length &&
    areOccupantsEqual(selectedRoomsPostAcknowledgement[0], TOUR_V2_DEFAULT_OCCUPANCIES[0]), [selectedRoomsPostAcknowledgement])

  const maxDeparturesToShowPerMonthIndex = useMemo<Map<App.MonthIndex, number>>(() => {
    const maxDeparturesPerMonthIndex = new Map<App.MonthIndex, number>()
    if (!departureYearsDisplayableMonthIndices.has(selectedDepartureYear)) {
      return maxDeparturesPerMonthIndex
    }
    let departuresToShow = maxDeparturesToShow
    for (const monthIndex of departureYearsDisplayableMonthIndices.get(selectedDepartureYear)) {
      if (categorisedDepartures.get(selectedDepartureYear).get(monthIndex).availableDepartures.length === 0) {
        maxDeparturesPerMonthIndex.set(monthIndex, null)
      } else if (departuresToShow === 0 || monthIndex < selectedDepartureMonthIndex) {
        maxDeparturesPerMonthIndex.set(monthIndex, 0)
      } else {
        const numberOfAvailableDepartures = categorisedDepartures.get(selectedDepartureYear).get(monthIndex).availableDepartures.length
        const monthDeparturesToShow = Math.min(departuresToShow, numberOfAvailableDepartures)
        departuresToShow -= monthDeparturesToShow
        maxDeparturesPerMonthIndex.set(monthIndex, monthDeparturesToShow)
      }
    }

    return maxDeparturesPerMonthIndex
  }, [categorisedDepartures, departureYearsDisplayableMonthIndices, maxDeparturesToShow, selectedDepartureMonthIndex, selectedDepartureYear])

  const numberOfAvailableDepaturesToShow = useMemo<number>(() =>
    sum(
      [...departureMonthIndicesToDisplay],
      monthIndex => categorisedDepartures.get(selectedDepartureYear).get(monthIndex).availableDepartures.length,
    ), [categorisedDepartures, departureMonthIndicesToDisplay, selectedDepartureYear])

  const numberOfDeparturesShown = useMemo<number>(() =>
    sum(
      [...maxDeparturesToShowPerMonthIndex.values()],
      (numberOfDepartures) => numberOfDepartures || 0,
    ), [maxDeparturesToShowPerMonthIndex])

  const nextDateWithAvailableDepartures = useMemo<{ year: number, monthIndex: App.MonthIndex }>(() => {
    let nextDepartureYear: number
    let nextDepartureMonthIndex: App.MonthIndex

    for (const [departureYear, departureMonthIndices] of departureYearsDisplayableMonthIndices) {
      if (nextDepartureYear) {
        break
      }
      if (departureYear <= selectedDepartureYear) {
        continue
      }
      for (const departureMonthIndex of departureMonthIndices) {
        if (categorisedDepartures.get(departureYear).get(departureMonthIndex).availableDepartures.length) {
          nextDepartureYear = departureYear
          nextDepartureMonthIndex = departureMonthIndex
          break
        }
      }
    }

    return nextDepartureYear !== undefined && nextDepartureMonthIndex !== undefined ?
        {
          year: nextDepartureYear,
          monthIndex: nextDepartureMonthIndex,
        } : null
  }, [categorisedDepartures, departureYearsDisplayableMonthIndices, selectedDepartureYear])

  const increaseMaxDeparturesToShow = useCallback(() => {
    setMaxDeparturesToShow(maxDeparturesToShow + INITIAL_MAX_DEPARTURES_TO_SHOW)
  }, [maxDeparturesToShow, setMaxDeparturesToShow])

  const selectYear = useCallback((year: number) => {
    offerPageDispatch({
      type: OfferPageActions.selectDepartureDate,
      year,
      monthIndex: departureYearsDisplayableMonthIndices.get(year)[0],
    })
    monthTabsRef.current.scrollLeft = 0
    setMaxDeparturesToShow(INITIAL_MAX_DEPARTURES_TO_SHOW)
  }, [departureYearsDisplayableMonthIndices, offerPageDispatch, setMaxDeparturesToShow])

  const selectMonthIndex = useCallback((monthIndex: App.MonthIndex) => {
    offerPageDispatch({
      type: OfferPageActions.selectDepartureMonthIndex,
      monthIndex,
    })
    setMaxDeparturesToShow(INITIAL_MAX_DEPARTURES_TO_SHOW)
  }, [offerPageDispatch, setMaxDeparturesToShow])

  const selectDate = useCallback((year: number, monthIndex: App.MonthIndex) => {
    offerPageDispatch({
      type: OfferPageActions.selectDepartureDate,
      year,
      monthIndex,
    })
    setMaxDeparturesToShow(INITIAL_MAX_DEPARTURES_TO_SHOW)
  }, [offerPageDispatch, setMaxDeparturesToShow])

  const selectNextAvailableDate = useCallback(() => {
    if (nextDateWithAvailableDepartures) {
      selectDate(nextDateWithAvailableDepartures.year, nextDateWithAvailableDepartures.monthIndex)
      controlsScrollAnchorRef.current.scrollIntoView()
    }
  }, [nextDateWithAvailableDepartures, selectDate])

  const toggleTravellersSelect = useCallback(() => {
    offerPageDispatch({
      type: OfferPageActions.toggleFilter,
      filter: 'capacity',
    })
  }, [offerPageDispatch])

  useEffect(() => {
    setMaxDeparturesToShow(INITIAL_MAX_DEPARTURES_TO_SHOW)
  }, [departureMonthIndicesToDisplay, setMaxDeparturesToShow])

  const {
    activeOccupantsIndex,
    isInEditMode,
    isModalOpen: isRoomSelectionModalOpen,
    selectedPurchasableOptions,
  } = roomSelectionState

  const closeRoomSelectionModal = useCallback(() => {
    roomSelectionStateDispatch({
      type: TourV2RoomSelectionActions.MODAL_TOGGLED,
      isModalOpen: false,
    })
  }, [roomSelectionStateDispatch])

  const proceedToCheckout = useCallback(() => {
    if (
      selectedDeparture &&
      selectedPurchasableOptions.size > 0 &&
      selectableOccupancies.every(occupants => !!selectedPurchasableOptions.get(occupants))
    ) {
      onProceedToCheckout(selectedDeparture.startDate, selectedDeparture.endDate, selectedPurchasableOptions, occupanciesTotalPrice)
    }
  }, [onProceedToCheckout, selectableOccupancies, selectedDeparture, selectedPurchasableOptions, occupanciesTotalPrice])

  const determineRoomSelection = useCallback(() => {
    let firstEdittableOccupantsIndex = null
    // preselect the option for occupants with only one option available
    for (const [occupantsIndex, occupants] of selectableOccupancies.entries()) {
      const options = selectedDepartureOccupanciesOptions.get(occupants)
      if (options.size === 1) {
        roomSelectionStateDispatch({
          type: TourV2RoomSelectionActions.PURCHASABLE_OPTION_SELECTED,
          occupants,
          purchasableOption: options.keys().next().value,
        })
      } else if (firstEdittableOccupantsIndex === null) {
        firstEdittableOccupantsIndex = occupantsIndex
      }
    }

    if (firstEdittableOccupantsIndex !== null) {
      roomSelectionStateDispatch({
        type: TourV2RoomSelectionActions.ACTIVE_OCCUPANTS_INDEX_SET,
        activeOccupantsIndex: firstEdittableOccupantsIndex,
      })
      if (config.TOUR_V2_NEW_CHECKOUT_ENABLED && hasPackageUpgrade && selectedDeparture) {
        onProceedToPackageUpgrade(selectedDeparture, selectableOccupancies)
      } else {
        roomSelectionStateDispatch({
          type: TourV2RoomSelectionActions.MODAL_TOGGLED,
          isModalOpen: true,
        })
      }
    }
  }, [hasPackageUpgrade, roomSelectionStateDispatch, selectableOccupancies, selectedDepartureOccupanciesOptions, selectedDeparture, onProceedToPackageUpgrade])

  const unselectDeparture = useCallback(() => {
    roomSelectionStateDispatch({ type: TourV2RoomSelectionActions.REINITIALISED })
    offerPageDispatch({ type: OfferPageActions.unselectTourV2OfferDeparture })
  }, [roomSelectionStateDispatch, offerPageDispatch])

  const selectDeparture = useCallback((departure: Tours.V2OfferDeparture) => {
    onDepartureSelect(departure)
    if (!rooms) {
      toggleTravellersSelect()
      if (controlsScrollAnchorRef?.current) {
        controlsScrollAnchorRef.current.scrollIntoView()
      }
      selectTempDeparture(departure)
    } else {
      offerPageDispatch({
        type: OfferPageActions.selectTourV2OfferDeparture,
        departure,
      })
      selectTempDeparture(null)
    }
  }, [
    rooms,
    toggleTravellersSelect,
    offerPageDispatch,
    onDepartureSelect,
  ])

  const handleTravellersSelection = useCallback(() => {
    if (!rooms) {
      offerPageDispatch({ type: OfferPageActions.setRooms, rooms: TOUR_V2_DEFAULT_OCCUPANCIES.map(generateRoomOccupants) })
    }
    // acknowledgeTravellersSelection(true)
  }, [rooms, offerPageDispatch])

  const selectOccupantsPurchasableOption = useCallback((occupants: App.Occupants, purchasableOption: Tours.V2OfferPurchasableOption) => {
    roomSelectionStateDispatch({
      type: TourV2RoomSelectionActions.PURCHASABLE_OPTION_SELECTED,
      occupants,
      purchasableOption,
    })
  }, [roomSelectionStateDispatch])

  const toggleEditMode = useCallback((isInEditMode: boolean) => {
    roomSelectionStateDispatch({
      type: TourV2RoomSelectionActions.EDIT_MODE_TOGGLED,
      isInEditMode,
    })
  }, [roomSelectionStateDispatch])

  const setActiveOccupantsIndex = useCallback((occupantsIndex: number | 'next') => {
    let changedActiveOccupantsIndex = null

    if (occupantsIndex === 'next') {
      // find the next occupants with more than one option available
      for (let index = activeOccupantsIndex + 1; index < selectableOccupancies.length; index++) {
        if (selectedDepartureOccupanciesOptions.get(selectableOccupancies[index]).size > 1) {
          changedActiveOccupantsIndex = index
          break
        }
      }
    } else {
      changedActiveOccupantsIndex = occupantsIndex
    }

    if (changedActiveOccupantsIndex >= 0 && changedActiveOccupantsIndex < selectableOccupancies.length) {
      roomSelectionStateDispatch({
        type: TourV2RoomSelectionActions.ACTIVE_OCCUPANTS_INDEX_SET,
        activeOccupantsIndex: changedActiveOccupantsIndex,
      })
    }
  }, [selectableOccupancies, activeOccupantsIndex, selectedDepartureOccupanciesOptions, roomSelectionStateDispatch])

  useEffect(() => {
    if (departureYearsDisplayableMonthIndices.size) {
      let initialDepartureYear: number
      let initialDepartureMonthIndex: App.MonthIndex

      for (const [departureYear, departureMonthIndices] of departureYearsDisplayableMonthIndices) {
        if (initialDepartureYear) {
          break
        }
        for (const departureMonthIndex of departureMonthIndices) {
          if (categorisedDepartures.get(departureYear).get(departureMonthIndex).availableDepartures.length) {
            initialDepartureYear = departureYear
            initialDepartureMonthIndex = departureMonthIndex
            break
          }
        }
      }

      if (!initialDepartureYear) {
        initialDepartureYear = departureYears[0]
        initialDepartureMonthIndex = categorisedDepartures.get(initialDepartureYear)[0]
      }

      selectDate(initialDepartureYear, initialDepartureMonthIndex)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer, selectedVariation])

  useEffect(() => {
    if (!previousRooms && rooms) {
      selectRoomsPostAcknowledgement(rooms)
    }
  }, [previousRooms, rooms])

  useEffect(() => {
    if (tempSelectedDeparture && hasSelectedDefaultRooms) {
      selectDeparture(tempSelectedDeparture)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelectedDefaultRooms, tempSelectedDeparture])

  useEffect(() => {
    if (selectedDeparture) {
      determineRoomSelection()
    }
  }, [selectedDeparture, determineRoomSelection])

  useEffect(() => {
    if (selectedDeparture && hasAllOccupanciesOptionsSelected && !isInEditMode) {
      Analytics.track('add-to-cart', addToCartEvent())
      proceedToCheckout()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAllOccupanciesOptionsSelected, isInEditMode, selectedDeparture, rooms])

  useEffect(() => {
    unselectDeparture()
    selectTempDeparture(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartureMonthIndex, selectedDepartureYear])

  const cheapestDeparture = offer.departures[cheapestPurchasableOption?.fkDepartureId]
  const cheapestPurchasableOptionDetails = useTourV2PurchasableOptionDetails(cheapestPurchasableOption)
  const depositsAvailable = !!cheapestDeparture && isTourV2DepositAllowed(
    cheapestDeparture.startDate,
    cheapestPurchasableOptionDetails.pricePerPerson,
    currentCurrency,
    offer.depositType,
  )

  const hasPrivateDeparture = !!offer.privateRequestKey

  return <Root size="md">
    <TitleContainer>
      <Heading variant="heading2" as="h2">Dates &amp; pricing</Heading>
    </TitleContainer>

    <ScrollAnchor ref={controlsScrollAnchorRef} vOffset={rem(110)} />
    <ControlsContainer>
      <TourV2TravellersSelect isPrivateTour={hasPrivateDeparture} offerType={offer.type} onApply={handleTravellersSelection} isLeTour={offer.isLeTour} />
      <YearTabButtonsGroup fit="grow-at-mobile">
        {departureYears.map(year => <TabButton
          key={year}
          size="medium"
          isActive={year === selectedDepartureYear}
          onClick={() => selectYear(year)}
        >
          {year}
        </TabButton>)}
      </YearTabButtonsGroup>
    </ControlsContainer>

    <MonthPickerContainer>
      <MonthTabButtonsGroup ref={monthTabsRef}>
        {departureYearsDisplayableMonthIndices.get(selectedDepartureYear)?.map((monthIndex) => <MonthTabButton
          key={`${selectedDepartureYear}-${monthIndex}`}
          monthIndex={monthIndex}
          startingPrice={availableMonthsStartingPrices.get(monthIndex)}
          numberOfAvailableDepartures={categorisedDepartures.get(selectedDepartureYear).get(monthIndex).availableDepartures.length}
          numberOfSoldOutDepartures={categorisedDepartures.get(selectedDepartureYear).get(monthIndex).soldOutDepartures.length}
          isActive={monthIndex === selectedDepartureMonthIndex}
          onMonthSelect={selectMonthIndex}
        />)}
      </MonthTabButtonsGroup>
    </MonthPickerContainer>

    {!!departureYearsDisplayableMonthIndices.size && !(hasChildExceedingMinAge && !hasPrivateDeparture) && <>
      {depositsAvailable && <SecureWithDepositBannerContainer>
        <SecureWithDepositBanner offerType={offer.type} depositAmountPercentage={offer.depositAmount} depositType={offer.depositType} depositAmount={offer.depositAmount} />
      </SecureWithDepositBannerContainer>}

      {!!tempSelectedDeparture && !!departureYearsDisplayableMonthIndices.size && <SelectedDepartureContainer>
        <DepartureCard
          departure={tempSelectedDeparture}
          isSelected
          endLocation={selectedVariation?.endLocation}
          occupanciesPriceBreakdowns={departuresOccupanciesPriceBreakdowns.get(tempSelectedDeparture)}
          cheapestPurchasableOption={departuresCheapestPurchasableOptions.get(tempSelectedDeparture)}
          startLocation={selectedVariation?.startLocation}
          onDepartureSelect={selectDeparture}
          onGuaranteedLabelClick={openGuaranteedDisclosureModal}
        />
      </SelectedDepartureContainer>}

      <DeparturesContainer>
        {Array.from(categorisedDepartures.entries()).map(([year, monthsDepartures]) =>
          Array.from(monthsDepartures.entries()).map(([monthIndex, monthDepartures]) => <MonthDepartures
            key={`${year}-${monthIndex}`}
            className={cn({
              'is-hidden':
                selectedDepartureYear !== year ||
                !departureMonthIndicesToDisplay.has(monthIndex) ||
                maxDeparturesToShowPerMonthIndex.get(monthIndex) === 0,
            })}
          >
            <Heading variant="heading6" format="titlecase">{MONTH_NAMES[monthIndex]} {year}</Heading>
            <TourV2MonthDepartures
              monthIndex={monthIndex}
              availableDepartures={monthDepartures.availableDepartures}
              soldOutDepartures={monthDepartures.soldOutDepartures}
              numberOfDeparturesToDisplay={maxDeparturesToShowPerMonthIndex.get(monthIndex)}
              onDepartureSelect={selectDeparture}
              onDepartureGuaranteedLabelClick={openGuaranteedDisclosureModal}
            />
          </MonthDepartures>))}
        <DeparturesFooterContainer>
          {numberOfAvailableDepaturesToShow > numberOfDeparturesShown && <TextButton
            kind="primary"
            onClick={increaseMaxDeparturesToShow}
          >
            Show more departures
          </TextButton>}
          {nextDateWithAvailableDepartures && numberOfAvailableDepaturesToShow <= numberOfDeparturesShown && <TextButton
            kind="primary"
            onClick={selectNextAvailableDate}
          >
            View departures in {nextDateWithAvailableDepartures.year}
          </TextButton>}
        </DeparturesFooterContainer>
      </DeparturesContainer>
    </>}

    <MessagesContainer>
      {((!hasPrivateDeparture && hasChildExceedingMinAge) || !departureYearsDisplayableMonthIndices.size) && <InvalidTravellersMessagePane
        minChildPriceAge={selectedVariation?.minChildPriceAge}
        onUpdateTravellersClick={toggleTravellersSelect}
      />}
    </MessagesContainer>

    <RoomSelectionModal
      activeOccupantsIndex={activeOccupantsIndex}
      isInEditMode={isInEditMode}
      isOpen={isRoomSelectionModalOpen}
      occupanciesOptions={selectedDepartureOccupanciesOptions}
      occupanciesTotalPrice={occupanciesTotalPrice}
      onAfterClose={unselectDeparture}
      hasAllOccupanciesOptionsSelected={hasAllOccupanciesOptionsSelected}
      selectedPurchasableOptions={selectedPurchasableOptions}
      selectableOccupancies={selectableOccupancies}
      onActiveOccupantsIndexChange={setActiveOccupantsIndex}
      onOccupantsPurchasableOptionSelect={selectOccupantsPurchasableOption}
      onClose={closeRoomSelectionModal}
      onSave={proceedToCheckout}
      onToggleEditMode={toggleEditMode}
    />

    <Modal isOpen={isGuaranteedDisclosureModalOpen} title="Guaranteed departures" onClose={closeGuaranteedDisclosureModal}>
      <BodyText variant="large">
        This tour is guaranteed to depart on the date scheduled as it has met the minimum required traveller numbers. Book now with confidence!
        <br />
        If you choose a non-guaranteed departure, there's a slight chance it may not go ahead if the minimum numbers are not met. In that case, we'll offer you a choice of another tour date, a similar tour, or a full refund so you can enjoy a worry-free tour experience!
      </BodyText>
    </Modal>
  </Root>
}

export default withOfferPageState<MappedStateProps>((offerPageState, ownProps: Props) => {
  const {
    roomSelectionState,
  } = ownProps

  const {
    hasChildExceedingMinAge,
  } = getHasChildExceedingAgeRange(offerPageState)

  return {
    categorisedDepartures: getCategorisedDepartures(offerPageState),
    availableMonthsStartingPrices: getAvailableMonthsStartingPrices(offerPageState),
    departureYears: getDepartureYears(offerPageState),
    cheapestPurchasableOption: getCheapestPurchasableOption(offerPageState),
    departuresAccommodableOptions: getDeparturesAccommodableOptions(offerPageState),
    departuresCheapestPurchasableOptions: getDeparturesCheapestPurchasableOptions(offerPageState),
    departuresOccupanciesPriceBreakdowns: getDeparturesOccupanciesPriceBreakdowns(offerPageState),
    hasAllOccupanciesOptionsSelected: getHasAllOccupanciesOptionsSelected(roomSelectionState, offerPageState),
    hasChildExceedingMinAge,
    occupanciesTotalPrice: getOccupanciesTotalPrice(roomSelectionState, offerPageState),
    offer: offerPageState.tourV2Offer,
    rooms: offerPageState.rooms,
    selectableOccupancies: getSelectableOccupancies(offerPageState),
    selectedDeparture: offerPageState.selectedTourV2OfferDeparture,
    selectedDepartureOccupanciesOptions: getSelectedDepartureOccupanciesOptions(offerPageState),
    hasPackageUpgrade: hasPackageUpgradeInSelectableOccupancies(offerPageState),
    selectedDepartureMonthIndex: offerPageState.selectedDepartureMonthIndex,
    selectedVariation: offerPageState.selectedTourV2OfferVariation,
    selectedDepartureYear: offerPageState.selectedDepartureYear,
    departureMonthIndicesToDisplay: getDepartureMonthIndicesToDisplay(offerPageState),
    departureYearsDisplayableMonthIndices: getDepartureYearsDisplayableMonthIndices(offerPageState),
  }
})(TourV2Departures)
